import { useCallback, useRef, useState } from 'react';
import { type ILogger } from '@bingads-webui/ads-copilot-shared-types';

export const AUTO_RELOAD_MAX_ATTEMPTS = 2;
export const AUTO_RELOAD_RESET_INTERVAL_MS = 10_000;

interface IAutoReloadGuardState {
  attempts: number;
  lastAttemptAt: number;
}

// DirectLineErrorView is remounted on every reload, so its local latch can't
// bound the loop; keep the count in a ref on the long-lived AdsCopilot instead.
export function useAutoReloadGuard(logger?: ILogger) {
  const stateRef = useRef<IAutoReloadGuardState>({ attempts: 0, lastAttemptAt: 0 });
  const [, forceRender] = useState(0);

  const canAutoReload = useCallback(() => {
    const { attempts, lastAttemptAt } = stateRef.current;
    if (Date.now() - lastAttemptAt >= AUTO_RELOAD_RESET_INTERVAL_MS) {
      return true;
    }
    return attempts < AUTO_RELOAD_MAX_ATTEMPTS;
  }, []);

  const recordAutoReload = useCallback(() => {
    const now = Date.now();
    const { attempts, lastAttemptAt } = stateRef.current;
    const withinBurst = now - lastAttemptAt < AUTO_RELOAD_RESET_INTERVAL_MS;
    const nextAttempts = withinBurst ? attempts + 1 : 1;
    stateRef.current = { attempts: nextAttempts, lastAttemptAt: now };

    if (nextAttempts >= AUTO_RELOAD_MAX_ATTEMPTS) {
      const message = `[Copilot][AutoReloadGuard] Token-expired auto-reload cap reached (${nextAttempts} attempts within ${AUTO_RELOAD_RESET_INTERVAL_MS}ms); stopping reacquisition loop and falling back to manual retry.`;
      logger?.trace(message);
    }

    forceRender(v => v + 1);
  }, [logger]);

  return { canAutoReload, recordAutoReload };
}
